import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Truck, RefreshCw, Shield, Star, Minus, Plus, ChevronRight } from 'lucide-react';
import Header from './components/Header2';
import Footer from './components/Footer2';
import Button from './components/Button';

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedSize, setSelectedSize] = useState('M');
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);
    const [isWishlisted, setIsWishlisted] = useState(false);

    const product = {
        id: id,
        name: 'Royal Banarasi Saree',
        price: 12999,
        originalPrice: 16999,
        rating: 4.8,
        reviews: 96,
        description: 'Exquisitely handwoven Banarasi saree featuring intricate zari work and traditional motifs. Crafted by master weavers from Varanasi, this saree embodies centuries of textile heritage with a contemporary colour palette.',
        features: [
            'Pure Banarasi silk with gold zari',
            'Handwoven by master artisans',
            'Includes matching blouse piece',
            'Traditional motifs with modern colours',
            'Lightweight and comfortable drape',
        ],
        images: [
            'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=800',
        ],
        sizes: ['XS', 'S', 'M', 'L', 'XL'],
    };

    const relatedProducts = [
        { id: 2, name: 'Silk Embroidered Lehenga', price: 24500, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Kanjivaram Silk Saree', price: 18999, badge: 'BESTSELLER', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Designer Anarkali Kurta', price: 8999, image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Indo-Western Fusion Gown', price: 15499, image: 'https://images.pexels.com/photos/3819793/pexels-photo-3819793.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    const discount = Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);

    return (
        <div className="w-full min-h-screen bg-[#0A0A0A]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
            <Header />

            <main className="pt-[80px] md:pt-[90px]">
                {/* Breadcrumb */}
                <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
                    <div className="flex items-center gap-2 text-xs text-[#525252] font-['Montserrat']">
                        <span className="cursor-pointer hover:text-[#FF6B35] transition-colors" onClick={() => navigate('/template2')}>Home</span>
                        <ChevronRight className="w-3 h-3" />
                        <span className="cursor-pointer hover:text-[#FF6B35] transition-colors" onClick={() => navigate('/template2/shop')}>Shop</span>
                        <ChevronRight className="w-3 h-3" />
                        <span className="text-[#A3A3A3]">{product.name}</span>
                    </div>
                </div>

                {/* Product Section */}
                <div className="max-w-[1400px] mx-auto px-4 md:px-6 pb-16">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                        {/* Image Gallery */}
                        <div>
                            <div className="aspect-[3/4] overflow-hidden bg-[#1A1A1A] mb-3">
                                <img
                                    src={product.images[selectedImage]}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                {product.images.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setSelectedImage(i)}
                                        className={`aspect-square overflow-hidden border-2 transition-colors ${
                                            selectedImage === i ? 'border-[#FF6B35]' : 'border-[#2A2A2A] hover:border-[#FF6B35]'
                                        }`}
                                    >
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Product Info */}
                        <div className="py-2 lg:py-4">
                            <h1 className="text-2xl md:text-3xl font-bold text-white mb-3 font-['Montserrat'] uppercase">
                                {product.name}
                            </h1>

                            {/* Rating */}
                            <div className="flex items-center gap-3 mb-4">
                                <div className="flex gap-0.5">
                                    {[...Array(5)].map((_, i) => (
                                        <Star
                                            key={i}
                                            className="w-4 h-4"
                                            style={{
                                                color: i < Math.floor(product.rating) ? '#FF6B35' : '#2A2A2A',
                                                fill: i < Math.floor(product.rating) ? '#FF6B35' : 'none',
                                            }}
                                        />
                                    ))}
                                </div>
                                <span className="text-sm text-[#A3A3A3] font-['Montserrat']">
                                    {product.rating} ({product.reviews} reviews)
                                </span>
                            </div>

                            {/* Price */}
                            <div className="flex items-center gap-3 mb-6">
                                <span className="text-2xl md:text-3xl font-bold text-[#FF6B35] font-['Montserrat']">
                                    {'\u20B9'}{product.price.toLocaleString('en-IN')}
                                </span>
                                <span className="text-lg text-[#525252] line-through font-['Montserrat']">
                                    {'\u20B9'}{product.originalPrice.toLocaleString('en-IN')}
                                </span>
                                <span className="bg-[#FF6B35] text-white text-xs font-bold px-2 py-1 font-['Montserrat']">
                                    {discount}% OFF
                                </span>
                            </div>

                            {/* Description */}
                            <p className="text-sm text-[#A3A3A3] leading-relaxed mb-6 font-['Montserrat']">
                                {product.description}
                            </p>

                            {/* Size Selection */}
                            <div className="mb-6">
                                <p className="text-xs font-semibold text-white uppercase tracking-wider mb-3 font-['Montserrat']">Size</p>
                                <div className="flex gap-2">
                                    {product.sizes.map((size) => (
                                        <button
                                            key={size}
                                            onClick={() => setSelectedSize(size)}
                                            className={`w-10 h-10 md:w-12 md:h-12 text-xs md:text-sm font-semibold transition-all duration-300 font-['Montserrat'] ${
                                                selectedSize === size
                                                    ? 'bg-[#FF6B35] text-white border border-[#FF6B35]'
                                                    : 'bg-transparent text-[#A3A3A3] border border-[#2A2A2A] hover:border-[#FF6B35]'
                                            }`}
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Quantity */}
                            <div className="mb-6">
                                <p className="text-xs font-semibold text-white uppercase tracking-wider mb-3 font-['Montserrat']">Quantity</p>
                                <div className="flex items-center border border-[#2A2A2A] w-fit">
                                    <button
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        className="w-10 h-10 flex items-center justify-center text-[#A3A3A3] hover:text-[#FF6B35] transition-colors"
                                    >
                                        <Minus className="w-4 h-4" />
                                    </button>
                                    <span className="w-12 h-10 flex items-center justify-center text-sm font-semibold text-white border-l border-r border-[#2A2A2A] font-['Montserrat']">
                                        {quantity}
                                    </span>
                                    <button
                                        onClick={() => setQuantity(quantity + 1)}
                                        className="w-10 h-10 flex items-center justify-center text-[#A3A3A3] hover:text-[#FF6B35] transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 mb-8">
                                <Button variant="primary" size="lg" className="flex-1 gap-2">
                                    <ShoppingCart className="w-4 h-4" /> Add to Cart
                                </Button>
                                <button
                                    onClick={() => setIsWishlisted(!isWishlisted)}
                                    className={`w-14 h-14 flex items-center justify-center border transition-all duration-300 ${
                                        isWishlisted ? 'border-[#FF6B35] bg-[#FF6B35]/10' : 'border-[#2A2A2A] hover:border-[#FF6B35]'
                                    }`}
                                >
                                    <Heart
                                        className="w-5 h-5"
                                        style={{
                                            color: isWishlisted ? '#FF6B35' : '#A3A3A3',
                                            fill: isWishlisted ? '#FF6B35' : 'none',
                                        }}
                                    />
                                </button>
                            </div>

                            {/* Features */}
                            <div className="border-t border-[#2A2A2A] pt-6 mb-6">
                                <p className="text-xs font-semibold text-white uppercase tracking-wider mb-3 font-['Montserrat']">Features</p>
                                <ul className="space-y-2">
                                    {product.features.map((feature, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm text-[#A3A3A3] font-['Montserrat']">
                                            <span className="w-1.5 h-1.5 bg-[#FF6B35] rounded-full mt-1.5 flex-shrink-0" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Trust Badges */}
                            <div className="grid grid-cols-3 gap-3 border-t border-[#2A2A2A] pt-6">
                                {[
                                    { icon: Truck, label: 'Free Shipping' },
                                    { icon: RefreshCw, label: 'Easy Returns' },
                                    { icon: Shield, label: 'Secure Payment' },
                                ].map(({ icon: Icon, label }) => (
                                    <div key={label} className="text-center">
                                        <Icon className="w-5 h-5 text-[#FF6B35] mx-auto mb-1.5" />
                                        <p className="text-[10px] md:text-xs text-[#A3A3A3] font-['Montserrat']">{label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Related Products */}
                    <div className="mt-16 md:mt-24">
                        <h2 className="text-xl md:text-2xl font-bold text-white mb-8 font-['Montserrat'] uppercase text-center">
                            You May Also Like
                        </h2>
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-6">
                            {relatedProducts.map((p) => (
                                <div
                                    key={p.id}
                                    onClick={() => navigate(`/template2/product/${p.id}`)}
                                    className="cursor-pointer group bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF6B35] transition-all duration-300"
                                >
                                    <div className="relative aspect-[3/4] overflow-hidden">
                                        <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        {p.badge && (
                                            <span className="absolute top-2 left-2 bg-[#FF6B35] text-white text-[10px] font-bold px-2 py-1 uppercase tracking-wider font-['Montserrat']">{p.badge}</span>
                                        )}
                                    </div>
                                    <div className="p-3">
                                        <h3 className="text-xs font-medium text-white line-clamp-1 mb-1 font-['Montserrat']">{p.name}</h3>
                                        <p className="text-xs font-bold text-[#FF6B35] font-['Montserrat']">{'\u20B9'}{p.price.toLocaleString('en-IN')}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default ProductDetail;
